import type { CurrencyInfo } from '@src/core/buyerApiClient';

import { getOrderSaleItems } from './getOrderSaleItems';
import type { Order, SaleCoupon } from '../types/order';

export const getOrderTotalCurrencyInfo = (order: Order | null | undefined): CurrencyInfo | undefined =>
  order?.finalAmountCurrencyInfo;

export const getOrderServiceFeeCurrencyInfo = (order: Order | null | undefined): CurrencyInfo | undefined =>
  order?.serviceFeeCurrencyInfo ?? undefined;

export const getOrderCoupon = (order: Order | null | undefined): SaleCoupon | null | undefined =>
  (order?.sales ?? []).map((sale) => sale.coupon).find(Boolean);

/** Metadata only (code/type/percentage). Discount amounts live in features/orders/summary. */
export const getOrderRepresentativeCoupon = (order: Order | null | undefined): SaleCoupon | undefined =>
  order?.sales.find((sale) => sale.coupon)?.coupon ?? undefined;

export const getOrderProductCount = (order: Order | null | undefined): number =>
  order ? getOrderSaleItems(order).length : 0;

export const getOrderSubtotalCents = (order: Order | null | undefined): number =>
  order?.partialAmountCurrencyInfo.amountCents ?? 0;

export const getOrderTotalAmountCents = (order: Order | null | undefined): number =>
  order?.finalAmountCurrencyInfo.amountCents ?? 0;

export const getOrderCouponCode = (order: Order | null | undefined): string | undefined =>
  getOrderRepresentativeCoupon(order)?.code;

export const isOrderCouponApplied = (order: Order | null | undefined): boolean => Boolean(getOrderCouponCode(order));

const sumCurrencyInfo = (a: CurrencyInfo, b: CurrencyInfo): CurrencyInfo => ({
  ...a,
  amountCents: a.amountCents + b.amountCents,
  originalAmountCents: a.originalAmountCents + b.originalAmountCents,
});

// The Order has no coupon of its own: the coupon is propagated to each sale, with that sale's amounts.
export const getOrderCombinedCoupon = (order: Order | null | undefined): SaleCoupon | undefined => {
  const coupons = (order?.sales ?? []).flatMap((sale) => (sale.coupon ? [sale.coupon] : []));

  if (coupons.length === 0) {
    return undefined;
  }

  return coupons.reduce((total, coupon) => ({
    ...total,
    fullAmountCents: total.fullAmountCents + coupon.fullAmountCents,
    fullAmountCurrencyInfo: sumCurrencyInfo(total.fullAmountCurrencyInfo, coupon.fullAmountCurrencyInfo),
    finalAmountCents: total.finalAmountCents + coupon.finalAmountCents,
    finalAmountCurrencyInfo: sumCurrencyInfo(total.finalAmountCurrencyInfo, coupon.finalAmountCurrencyInfo),
  }));
};

export const getOrderSummarySubtotalCurrencyInfo = (order: Order): CurrencyInfo =>
  getOrderCombinedCoupon(order) ? order.fullAmountCurrencyInfo : order.partialAmountCurrencyInfo;
